import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Asset, AssetStatus } from "../__generated__/graphql";
import { GET_ASSET } from "../graphql/asset";

type AssetWaitingResult = {
    waiting: boolean;
    error: string | null;
    asset?: Asset | null;
    reset: () => void;
};

/**
 * Wait for the asset to be ready or error, if the asset is not ready, it will poll the server every 5 seconds
 * @param assetId
 */
export const useAssetWaiting = (assetId?: string): AssetWaitingResult => {
    const [loadedAsset, setLoadedAsset] = useState<Asset | null>(null);
    const [isReady, setIsReady] = useState(false);

    const [error, setError] = useState<string | null>(assetId ? null : "Asset ID is empty");

    const {
        data,
        error: gqlError,
        refetch,
    } = useQuery(GET_ASSET, {
        fetchPolicy: "network-only",
        variables: { assetId: assetId || "" },
        pollInterval: 5000,
        skip: !assetId || !!loadedAsset || !!error || isReady,
    });

    const reset = useCallback(() => {
        setLoadedAsset(null);
        setError(assetId ? null : "Asset ID is empty");
        void refetch();
    }, [refetch, assetId]);

    useEffect(() => {
        setLoadedAsset((old) => {
            if (assetId) {
                if (old && old.id == assetId) {
                    return old;
                } else {
                    setError(null);
                    setIsReady(false);
                    return null;
                }
            } else {
                setError("Asset ID is empty");
                return null;
            }
        });
    }, [assetId]);

    useEffect(() => {
        if (gqlError) {
            setError(gqlError?.message || null);
        }
    }, [gqlError]);

    useEffect(() => {
        const asset = data?.getAsset || null;
        if (asset && asset.status != AssetStatus.Pending) {
            setLoadedAsset(asset);
        }
        if (asset && asset.status == AssetStatus.Ready) {
            setIsReady(true);
        }
    }, [data?.getAsset]);

    const waiting = useMemo(() => {
        /**
         * the waiting status is only available is assetId is set and there is no error while loading
         * loadedAsset hold the asset that is not pending, means that is i is present, the waiting is over.
         */
        if (!assetId) {
            return false;
        }
        if (error) {
            return false;
        }
        return !loadedAsset;
    }, [assetId, error, loadedAsset]);
    return {
        waiting,
        error,
        asset: loadedAsset,
        reset,
    };
};
